import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, ChevronDown, Check, Plus, X, Tag, FolderTree } from 'lucide-react';
import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

export interface CategoryOption {
    id: string;
    name: string;
    slug?: string;
    parentId: string | null;
}

interface CategoryComboboxProps {
    categories: CategoryOption[];
    selectedId: string;
    onSelect: (id: string) => void;
    onCreateNew: () => void;
    error?: string;
    disabled?: boolean;
}

export function CategoryCombobox({
    categories = [],
    selectedId,
    onSelect,
    onCreateNew,
    error,
    disabled = false,
}: CategoryComboboxProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Map parent categories for hierarchical breadcrumbs
    const categoryMap = useMemo(() => {
        const map = new Map<string, CategoryOption>();
        categories.forEach(c => map.set(c.id, c));
        return map;
    }, [categories]);

    // Selected category object
    const selectedCategory = useMemo(() => {
        return categories.find(c => c.id === selectedId);
    }, [categories, selectedId]);

    // Filter categories based on search
    const filteredCategories = useMemo(() => {
        if (!searchTerm.trim()) return categories;
        const term = searchTerm.toLowerCase().trim();
        return categories.filter(c => {
            const matchesName = c.name.toLowerCase().includes(term);
            const parent = c.parentId ? categoryMap.get(c.parentId) : null;
            const matchesParent = parent ? parent.name.toLowerCase().includes(term) : false;
            return matchesName || matchesParent;
        });
    }, [categories, searchTerm, categoryMap]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when opened
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        } else if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    const handleSelect = (id: string) => {
        onSelect(id);
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect('');
    };

    return (
        <div ref={containerRef} className="relative">
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Category <span className="text-rose-500">*</span>
            </label>

            {/* Combobox Trigger Button */}
            <div
                role="button"
                tabIndex={0}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (!disabled) setIsOpen(!isOpen);
                    }
                }}
                className={clsx(
                    'w-full px-4 py-3 border-2 rounded-xl text-sm flex items-center justify-between cursor-pointer transition-colors',
                    error
                        ? 'border-rose-300 bg-rose-50/50 dark:bg-rose-950/20 dark:border-rose-800'
                        : isOpen
                        ? 'border-emerald-500 ring-2 ring-emerald-500/20 bg-white dark:bg-slate-900 dark:border-emerald-500'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-600',
                    disabled && 'opacity-60 cursor-not-allowed bg-slate-100 dark:bg-slate-800'
                )}
            >
                <div className="flex items-center gap-2 truncate">
                    {selectedCategory ? (
                        <>
                            <Tag className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                            {selectedCategory.parentId && (
                                <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                                    {categoryMap.get(selectedCategory.parentId)?.name || 'Parent'}
                                    <span className="text-slate-300 dark:text-slate-600">/</span>
                                </span>
                            )}
                            <span className="font-medium text-slate-800 dark:text-slate-200 truncate">
                                {selectedCategory.name}
                            </span>
                        </>
                    ) : (
                        <span className="text-slate-400 dark:text-slate-500">Search or select category...</span>
                    )}
                </div>

                <div className="flex items-center gap-1">
                    {selectedCategory && !disabled && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                            title="Clear category"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <ChevronDown className={clsx('w-4 h-4 text-slate-400 transition-transform duration-200', isOpen && 'rotate-180')} />
                </div>
            </div>

            {error && <p className="text-xs text-rose-500 mt-1">{error}</p>}

            {/* Dropdown Popover */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                        className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden"
                    >
                        {/* Search Input Bar */}
                        <div className="p-2 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
                            <div className="relative">
                                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Type to filter categories..."
                                    className="w-full pl-9 pr-3 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:text-slate-200"
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Category List */}
                        <div className="max-h-56 overflow-y-auto divide-y divide-slate-50 dark:divide-slate-800">
                            {filteredCategories.length === 0 ? (
                                <div className="p-4 text-center">
                                    <p className="text-xs text-slate-400 dark:text-slate-500">No categories found matching "{searchTerm}"</p>
                                </div>
                            ) : (
                                filteredCategories.map((c) => {
                                    const isSelected = c.id === selectedId;
                                    const parent = c.parentId ? categoryMap.get(c.parentId) : null;

                                    return (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={() => handleSelect(c.id)}
                                            className={clsx(
                                                'w-full text-left px-4 py-2.5 text-xs flex items-center justify-between transition-colors',
                                                isSelected
                                                    ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 font-semibold'
                                                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300'
                                            )}
                                        >
                                            <div className="flex items-center gap-2 truncate">
                                                {c.parentId ? (
                                                    <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                                        <FolderTree className="w-2.5 h-2.5" />
                                                        {parent?.name || 'Sub'}
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-100/70 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                                                        Main
                                                    </span>
                                                )}
                                                <span className="truncate">{c.name}</span>
                                            </div>

                                            {isSelected && <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />}
                                        </button>
                                    );
                                })
                            )}
                        </div>

                        {/* Inline "+ Create New Category" Button */}
                        <div className="p-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsOpen(false);
                                    onCreateNew();
                                }}
                                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 rounded-lg transition-colors border border-emerald-200/60 dark:border-emerald-800/60"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Create New Category
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
